import { useEffect, useState } from "react";
import {
  FaVideo,
  FaPlay,
  FaAngleDown
} from "react-icons/fa6";
import { FiFilter, FiSend } from "react-icons/fi";
import { IoVideocamOutline } from "react-icons/io5";
import { Card, CardContent } from "../../components/ui/card";
import Button from "../../components/button-v1/index";
import CustomInput from "../../components/ui/input";
import { Dropdown } from "../../components/ui/dropdown";
import SendModal from "../admin/instructional-video-dashboard/send-video/sendVideoModal";
import PlayVideoModal from "../admin/instructional-video-dashboard/play-video/playVideoModal";
import Spinner from "../../components/spinner";
import styles from "../../styles/InstructionalVideos.module.css";
import { usePatientPageData } from "../../utils/youtubeApiQueries";
import { parseDateSafe } from "../../utils/dateFormatters";

const parseDuration = (pt) => {
  if (!pt) return "0:00";
  if (typeof pt === "string" && pt.includes(":")) return pt;
  const hours = pt.match(/(\d+)H/);
  const minutes = pt.match(/(\d+)M/);
  const seconds = pt.match(/(\d+)S/);
  const h = hours ? parseInt(hours[1]) : 0;
  const m = minutes ? parseInt(minutes[1]) : 0;
  const s = seconds ? parseInt(seconds[1]) : 0;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = parseDateSafe(dateString);
  if (!date) return "";
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const InstructionalVideos = ({ currentPatient }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showPlayModal, setShowPlayModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);

  const { videos, categories: categoriesData, isLoading, error } = usePatientPageData();

  // Categories for dropdown
  const categories = [
    { value: "all", label: "All Categories" },
    ...categoriesData.map(cat => ({
      value: cat.id,
      label: cat.name
    }))
  ];

  // Filter videos based on search and category
  const filteredVideos = videos.filter(video => {
    const matchesCategory = selectedCategory === "all" || video.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      video.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Helper functions for instructional videos
  const handleVideoSelect = (videoId) => {
    setSelectedVideos(prev =>
      prev.includes(videoId)
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  };

  const videosByCategory = filteredVideos.reduce((acc, video) => {
    const categoryName = video.category_name || 'Uncategorized';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(video);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className={styles.spinner_container}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.spinner_container}>
        <div>
          <p>Error loading videos. Please try again.</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.dashboard} style={{ backgroundColor: 'white', borderRadius: '20px' }}>
        <div className={styles.controls}>
          <div className={styles.searchContainer}>
            <CustomInput
              placeholder="Search videos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
              startIcon={<FaVideo />}
            />
          </div>
          <div className={styles.filterContainer}>
            <Dropdown
              options={categories}
              value={selectedCategory}
              onChange={setSelectedCategory}
              placeholder="All Categories"
              trigger={
                <div className={styles.dropdownTrigger}>
                  <FiFilter className={styles.dropdownFilterIcon} />
                  <span className={styles.dropdownInput}>
                    {categories.find(cat => cat.value === selectedCategory)?.label || "All Categories"}
                  </span>
                  <FaAngleDown className={styles.dropdownArrow} />
                </div>
              }
            />
          </div>
        </div>

        <div className={styles.videoCategories}>
          {Object.entries(videosByCategory).map(([category, categoryVideos]) => (
            <div key={category} className={styles.categorySection} style={{ backgroundColor: 'white' }}>
              <div className={styles.categoryHeader}>
                <div className={styles.categoryLeft}>
                  <div className={styles.categoryIcon}></div>
                  <h2 className={styles.categoryTitle}>{category}</h2>
                </div>
                <span className={styles.countBadge}>
                  {categoryVideos.length} {categoryVideos.length === 1 ? "video" : "videos"}
                </span>
              </div>
              <div className={styles.videoGrid}>
                {categoryVideos.map(video => (
                  <Card key={video.uuid} className={styles.videoCard}>
                    <div 
                      className={styles.videoThumbnail}
                      onClick={() => {
                        setCurrentVideo(video);
                        setShowPlayModal(true);
                      }}
                    >
                      {video.youtube_video_id ? (
                        <img 
                          src={`https://img.youtube.com/vi/${video.youtube_video_id}/hqdefault.jpg`} 
                          alt={video.title}
                          className={styles.thumbnailImage}
                        />
                      ) : (
                        <IoVideocamOutline className={styles.cameraIcon} />
                      )}
                      <div className={styles.duration}>{parseDuration(video.duration)}</div>
                      <div className={styles.playOverlay}>
                        <FaPlay className={styles.playIcon} />
                      </div>
                    </div>
                    <CardContent className={styles.videoContent}>
                      <div className={styles.videoHeader}>
                        <div className={styles.videoInfo}>
                          <h3 className={styles.videoTitle}>{video.title}</h3>
                          <p className={styles.videoDescription}>{video.description}</p>
                        </div>
                      </div>
                      <div className={styles.videoActions}>
                        <p className={styles.videoDate}>{formatDate(video.created_at)}</p>
                        <Button
                          size="sm"
                          className={`${styles.selectBadge} ${selectedVideos.includes(video.uuid) ? styles.selected : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVideoSelect(video.uuid);
                          }}
                        >
                          {selectedVideos.includes(video.uuid) ? "Selected" : "Select"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>

        {filteredVideos.length === 0 && (
          <div className={styles.noResults}>
            <FaVideo className={styles.noResultsIcon} />
            <p>No videos found matching your criteria.</p>
          </div>
        )}

        <div className={styles.selectionFooter} style={{ backgroundColor: 'white' }}>
          <p className={styles.selectionText}>Select videos to send to patients or clients</p>
          <Button 
            disabled={selectedVideos.length === 0}
            onClick={() => selectedVideos.length > 0 && setShowSendModal(true)}
            className={styles.sendVideosButton}
          >
            <FiSend /> Send Videos
          </Button>
        </div>
      </div>

      {showSendModal && (
        <SendModal
          open={showSendModal}
          onClose={() => setShowSendModal(false)}
          selectedVideos={selectedVideos}
          allVideos={videos}
          onToggleSelect={handleVideoSelect}
          currentPatient={currentPatient}
        />
      )}

      {showPlayModal && (
        <PlayVideoModal
          open={showPlayModal}
          onClose={() => setShowPlayModal(false)}
          video={currentVideo}
        />
      )}
    </>
  );
};

export default InstructionalVideos;
